import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import Config, { getServerListPayload } from "../../../../../config/Config";
import api from "../../../../../store/api";
import { API_URL } from "./apiUrls";
import { AppThunk, RootState } from "../../../../../store/app";
import { GridOptions, GridReadyEvent, IServerSideGetRowsParams } from "ag-grid-community";
import { convertTextToID, isActiveStatusValue } from "../../../../../config/Global";

interface InitialState {
    isLoading: boolean;
    agGrid: any;
    perPage: any;
    details: any;
    villageDetails: any,
    religionDetails: any,
}
const initialState = {
    isLoading: false,
    agGrid: null,
    perPage: Config.grid.server.gridOptions?.paginationPageSize,
    details: null,
    villageDetails: null,
    religionDetails: null,
} as InitialState;


const villageDetailsSlice = createSlice({
    name: "villageDetails",
    initialState,
    reducers: {
        start: state => {
            state.isLoading = true;
        },
        success: state => {
            state.isLoading = false;
        },
        failure: state => {
            state.isLoading = false;
        },
        setGrid: (state, action: PayloadAction<any>) => {
            state.agGrid = action?.payload;
        },
        setPerPage: (state, action: PayloadAction<any>) => {
            state.perPage = action?.payload;
            if (state.agGrid) {
                state.agGrid.api.paginationSetPageSize(Number(state.perPage));
                state.agGrid.api.setCacheBlockSize(state.perPage);
            }
        },
        setDetails: (state, action: PayloadAction<any>) => {
            state.details = action?.payload;
        },
        setVillageDetails: (state, action: PayloadAction<any>) => {
            state.villageDetails = action?.payload;
        },
        setReligionList: (state, action: PayloadAction<any>) => {
            state.religionDetails = action?.payload;
        }
    },
});

export const { start, success, failure, setGrid, setPerPage, setDetails, setVillageDetails, setReligionList } =
    villageDetailsSlice.actions;


/** For Listing:Start */
const fetchList = async (payload?: any): Promise<any> => {
    return await api
        .post(API_URL.LIST, payload)
        .then(({ data }) => {
            return data;
        })
        .catch(error => error);
};

export const setPerPageSize =
    (size: number): AppThunk<any> =>
        async (dispatch, getState: () => RootState) => {
            dispatch(setPerPage(size));
            dispatch(setupGrid(getState().villageDetails.agGrid));
        };

export const setupGrid =
    (params: GridReadyEvent): AppThunk<any> =>
        async (dispatch, getState: () => RootState) => {
            try {
                dispatch(setGrid(params));
                const dataSource = await createDataSource(
                    getState,
                    Config.grid.server.gridOptions
                );
                params.api.setServerSideDatasource(dataSource);
            } catch (error: any) {
                //
            }
        };

const changeFilterAndSort = (params: any) => {
    params;
    if (params.filterModel && params.filterModel["is_active"]) {
        params.filterModel["is_active"].values = convertTextToID(
            params.filterModel["is_active"],
            isActiveStatusValue
        );
    }
};

const createDataSource = (
    getState: () => RootState,
    gridOptions?: GridOptions
) => {
    return {
        gridOptions,
        getRows: (params: IServerSideGetRowsParams) => {
            changeFilterAndSort(params.request);
            const payload = getServerListPayload(params);
            fetchList(payload).then(data => {
                if (data.count > 0) {
                    const startPage = (payload.page - 1) * payload.per_page;
                    data.rows.map((item: any, index: number) => {
                        item.srno = startPage + index + 1;
                        return null;
                    });
                }
                params.success({ rowData: data?.rows, rowCount: data?.count });
                if (data.count <= 0) {
                    params.api.showNoRowsOverlay();
                } else {
                    params.api.hideOverlay();
                }
            }).catch((error: any) => error);
        },
    };
};

/** For Listing:End */

export const createRecord =
    (action: any): AppThunk<any> =>
        async (dispatch, getState) => {
            try {
                dispatch(start());
                const response = await api.post(API_URL.CREATE, action);
                dispatch(setupGrid(getState().villageDetails.agGrid));
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };

export const updateRecord =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch, getState) => {
            try {
                dispatch(start());
                const response = await api.post(API_URL.UPDATE(id), action);
                dispatch(setupGrid(getState().villageDetails.agGrid));
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };

export const deleteRecord =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch, getState) => {
            try {
                dispatch(start());
                const response = await api.delete(API_URL.DELETE(id), action);
                dispatch(setupGrid(getState().villageDetails.agGrid));
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };

export const details =
    (id: number): AppThunk<any> =>
        async dispatch => {
            try {
                const response = await api.get(API_URL.DETAILS(id));
                dispatch(setDetails(response.data.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                return Promise.reject(error.data);
            }
        };

export const changeStatus =
    (id: number, is_active: number): AppThunk<any> =>
        async (dispatch, getState) => {
            try {
                const response = await api.post(API_URL.CHANGE_STATUS(id), { is_active: is_active });
                dispatch(setupGrid(getState().villageDetails.agGrid));
                return Promise.resolve(response.data);
            } catch (error: any) {
                return Promise.reject(error.data);
            }
        };

// add village details and party worker inner form and details
export const addVillageDetails =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                dispatch(start());
                const response = await api.post(API_URL.VILLAGE_DETAILS, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const updateVillageDetails =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_DETAILS_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };

export const getWorkerList = (action: any): AppThunk<any> =>
    async (dispatch) => {
        try {
            const response = await api.post(API_URL.PARTY_WORKER_LIST, action);
            dispatch(success(response.data));
            return Promise.resolve(response.data);
        } catch (error: any) {
            dispatch(failure(error.data));
            return Promise.reject(error.data);
        }
    };
export const addPartyWorker =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.PARTY_WORKER_ADD, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const deletePartyWorker =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.delete(API_URL.PARTY_WORKER_DELETE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const updatePartyWorker =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.PARTY_WORKER_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };

export const getMalnutrionedChildrensList = (action: any): AppThunk<any> =>
    async (dispatch) => {
        try {
            const response = await api.post(API_URL.MALNUTRIONED_CHILDRENS_LIST, action);
            dispatch(success(response.data));
            return Promise.resolve(response.data);
        } catch (error: any) {
            dispatch(failure(error.data));
            return Promise.reject(error.data);
        }
    };
export const addMalnutrionedChildrens =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.MALNUTRIONED_CHILDRENS_ADD, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const deleteMalnutrionedChildrens =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.delete(API_URL.MALNUTRIONED_CHILDRENS_DELETE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const updateMalnutrionedChildrens =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.MALNUTRIONED_CHILDRENS_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
// add village details and Villege Gove Scheme inner form and details
export const addVillageGovernmentSchemes =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_GOVE_SCHEMES_ADD, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const deleteVillageGovernmentSchemes =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.delete(API_URL.VILLAGE_GOVE_SCHEMES_DELETE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const updateVillageGovernmentSchemes =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_GOVE_SCHEMES_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
// add village details and Villege Unavailable Service inner form and details
export const addVillageUnavailableServices =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_UNAVAILABLE_SCHEMES_ADD, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const deleteVillageUnavailableServices =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.delete(API_URL.VILLAGE_UNAVAILABLE_SCHEMES_DELETE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const updateVillageUnavailableServices =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_UNAVAILABLE_SCHEMES_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
// add village details and Villege Problems inner form and details
export const addVillageProblems =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_PROBLEM_ADD, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const deleteVillageProblems =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.delete(API_URL.VILLAGE_PROBLEM_DELETE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const updateVillageProblems =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_PROBLEM_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };

// add village details and Villege Achievement inner form and details
export const addVillageAchievements =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_ACHIEVEMENTS_ADD, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const deleteVillageAchievements =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.delete(API_URL.VILLAGE_ACHIEVEMENTS_DELETE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const updateVillageAchievements =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_ACHIEVEMENTS_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };

// add village details and Villege Festivals inner form and details
export const addVillageFestival =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_FESTIVALS_ADD, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const deleteVillageFestival =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.delete(API_URL.VILLAGE_FESTIVALS_DELETE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const updateVillageFestival =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_FESTIVALS_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
// add village details and Villege Attraction inner form and details
export const addVillageAttraction =
    (action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_ATTRACTION_ADD, action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const deleteVillageAttraction =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.delete(API_URL.VILLAGE_ATTRACTION_DELETE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };

export const updateVillageAttraction =
    (id: number, action: any): AppThunk<any> =>
        async (dispatch) => {
            try {
                const response = await api.post(API_URL.VILLAGE_ATTRACTION_UPDATE(id), action);
                dispatch(success(response.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(failure(error.data));
                return Promise.reject(error.data);
            }
        };
export const getReligionsloc =
    (payload?: any): AppThunk<any> =>
        async dispatch => {
            try {
                const response = await api.post(API_URL.VILLAGE_FESTIVALS_Religions(), payload);
                dispatch(setReligionList(response.data.data))
                return Promise.resolve(response.data);
            } catch (error: any) {
                dispatch(setReligionList([]))
                return Promise.reject(error.data);
            }
        }

export const viewVillageDetails =
    (id: number): AppThunk<any> =>
        async dispatch => {
            try {
                const response = await api.get(API_URL.VILLAGE_DETAILS_VIEW(id));
                dispatch(setVillageDetails(response.data.data));
                return Promise.resolve(response.data);
            } catch (error: any) {
                return Promise.reject(error.data);
            }
        };
export const getGovSchemesList = (action: any): AppThunk<any> =>
    async (dispatch) => {
        try {
            const response = await api.post(API_URL.VILLAGE_GOV_SCHEMES_LIST, action);
            dispatch(success(response.data));
            return Promise.resolve(response.data);
        } catch (error: any) {
            dispatch(failure(error.data));
            return Promise.reject(error.data);
        }
    };
export const getUnavailableServicesList = (action: any): AppThunk<any> =>
    async (dispatch) => {
        try {
            const response = await api.post(API_URL.VILLAGE_UNAVAILABLE_SERVICES, action);
            dispatch(success(response.data));
            return Promise.resolve(response.data);
        } catch (error: any) {
            dispatch(failure(error.data));
            return Promise.reject(error.data);
        }
    };
export const getVillageProblemList = (action: any): AppThunk<any> =>
    async (dispatch) => {
        try {
            const response = await api.post(API_URL.VILLAGE_PROBLEMS, action);
            dispatch(success(response.data));
            return Promise.resolve(response.data);
        } catch (error: any) {
            dispatch(failure(error.data));
            return Promise.reject(error.data);
        }
    };
export const getVillageAchievementsList = (action: any): AppThunk<any> =>
    async (dispatch) => {
        try {
            const response = await api.post(API_URL.VILLAGE_ACHIEVEMENTS, action);
            dispatch(success(response.data));
            return Promise.resolve(response.data);
        } catch (error: any) {
            dispatch(failure(error.data));
            return Promise.reject(error.data);
        }
    };
export const getVillageFestivalsList = (action: any): AppThunk<any> =>
    async (dispatch) => {
        try {
            const response = await api.post(API_URL.VILLAGE_FESTIVALS, action);
            dispatch(success(response.data));
            return Promise.resolve(response.data);
        } catch (error: any) {
            dispatch(failure(error.data));
            return Promise.reject(error.data);
        }
    };
export const getVillageAttractionList = (action: any): AppThunk<any> =>
    async (dispatch) => {
        try {
            const response = await api.post(API_URL.VILLAGE_ATTRACTION, action);
            dispatch(success(response.data));
            return Promise.resolve(response.data);
        } catch (error: any) {
            dispatch(failure(error.data));
            return Promise.reject(error.data);
        }
    };
const userReducer = villageDetailsSlice.reducer;
export default userReducer;